import { HttpErrorResponse, HttpEvent, HttpHandler, HttpInterceptor, HttpRequest } from '@angular/common/http'
import { Injectable } from '@angular/core'
import { Router } from '@angular/router'
import { BehaviorSubject, Observable, first, switchMap, throwError } from 'rxjs'
import { catchError } from 'rxjs/operators'
import { AppRoutes } from '../../shared/constants/enums'
import { AuthService } from '../services/auth.service'

@Injectable()
export class AuthInterceptor implements HttpInterceptor {
    private isRefreshingAuthorization: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false)

    constructor(
        private authService: AuthService,
        private router: Router
    ) {}

    intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
        const token = this.authService.currentRepresentativeAccessToken.getValue()
        const newRequest: HttpRequest<any> =
            token && !this.isRefreshingAuthorization.getValue() ? this.getRequestWithAuthHeader(req, token) : req
        return next.handle(newRequest).pipe(
            catchError((errorResponse: HttpErrorResponse) => {
                if (errorResponse.status === 401) {
                    return this.handle401(newRequest, next, errorResponse)
                }
                return throwError(() => errorResponse)
            })
        )
    }

    private getRequestWithAuthHeader(req: HttpRequest<any>, token: string): HttpRequest<any> {
        const headers = req.headers.set('Authorization', 'Bearer ' + token)
        return req.clone({ headers })
    }

    private handle401(
        req: HttpRequest<any>,
        next: HttpHandler,
        errorResponse: HttpErrorResponse
    ): Observable<HttpEvent<any>> {
        if (!this.isRefreshingAuthorization.getValue()) {
            this.isRefreshingAuthorization.next(true)
            if (this.authService.currentRepresentativeRefreshToken.getValue()) {
                return this.authService.refreshAuthorization$().pipe(
                    switchMap((request) => {
                        this.isRefreshingAuthorization.next(false)
                        return next.handle(this.getRequestWithAuthHeader(req, request.accessToken))
                    }),
                    catchError((errorResponse: HttpErrorResponse) => {
                        if (errorResponse.status === 401) {
                            this.router.navigate(['/', AppRoutes.LOGGED_OUT])
                        }
                        this.isRefreshingAuthorization.next(false)
                        return throwError(() => errorResponse)
                    })
                )
            }
            return next.handle(req)
        }
        // if refreshAuthorization is pending, just wait for refreshed tokens and make new request
        // or when new token is not found, just reject with first error response (don't send new request)
        return this.isRefreshingAuthorization.pipe(
            first((isRefreshing) => !isRefreshing),
            switchMap(() => {
                const token = this.authService.currentRepresentativeAccessToken.getValue()
                return token ? next.handle(this.getRequestWithAuthHeader(req, token)) : throwError(() => errorResponse)
            })
        )
    }
}
